// AdminBillingPage — Subscription management
// Cancel, refund, change tier, prorate, view MRR/churn

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Subscription {
  id: string;
  trader: string;
  avatar: number;
  account: string;
  tier: 'Beginner' | 'Mid' | 'Professional';
  accountSize: number;
  mrr: number;
  started: string;
  nextBilling: string;
  status: 'active' | 'past-due' | 'cancelled' | 'paused';
  churnRisk: 'low' | 'medium' | 'high';
  failedPayments: number;
}

const SUBS: Subscription[] = [
  { id: 's1', trader: 'Marcus J.', avatar: 1, account: 'FP-100K-14283', tier: 'Mid',          accountSize: 100000, mrr: 149, started: '2026-04-12', nextBilling: '2026-08-12', status: 'active',   churnRisk: 'low',    failedPayments: 0 },
  { id: 's2', trader: 'Sarah C.',  avatar: 2, account: 'FP-150K-12844', tier: 'Professional', accountSize: 150000, mrr: 219, started: '2026-05-01', nextBilling: '2026-08-01', status: 'active',   churnRisk: 'low',    failedPayments: 0 },
  { id: 's3', trader: 'Riley T.',  avatar: 3, account: 'FP-50K-15201',  tier: 'Beginner',     accountSize: 50000,  mrr: 79,  started: '2026-07-04', nextBilling: '2026-08-04', status: 'active',   churnRisk: 'medium', failedPayments: 1 },
  { id: 's4', trader: 'Devon K.',  avatar: 4, account: 'FP-100K-13892', tier: 'Mid',          accountSize: 100000, mrr: 149, started: '2026-06-12', nextBilling: '2026-07-12', status: 'past-due', churnRisk: 'high',   failedPayments: 2 },
  { id: 's5', trader: 'Felix W.',  avatar: 1, account: 'FP-50K-14672',  tier: 'Beginner',     accountSize: 50000,  mrr: 79,  started: '2026-05-22', nextBilling: '2026-08-22', status: 'active',   churnRisk: 'low',    failedPayments: 0 },
  { id: 's6', trader: 'Lisa H.',   avatar: 4, account: 'FP-50K-15034',  tier: 'Beginner',     accountSize: 50000,  mrr: 0,   started: '2026-03-15', nextBilling: '—',         status: 'cancelled', churnRisk: 'low',  failedPayments: 0 },
  { id: 's7', trader: 'Brent O.',  avatar: 3, account: 'FP-100K-14821', tier: 'Mid',          accountSize: 100000, mrr: 0,   started: '2026-07-10', nextBilling: '—',         status: 'paused',   churnRisk: 'medium', failedPayments: 0 },
];

const TIER_COLOR = { Beginner: '#34C759', Mid: '#0A84FF', Professional: '#1C1C1E' };
const STATUS_META: Record<string, { color: string; bg: string }> = {
  'active':    { color: '#34C759', bg: 'rgba(52,199,89,0.15)' },
  'past-due':  { color: '#FF3B30', bg: 'rgba(255,59,48,0.15)' },
  'cancelled': { color: '#8E8E93', bg: 'rgba(142,142,147,0.15)' },
  'paused':    { color: '#FF9500', bg: 'rgba(255,149,0,0.15)' },
};
const RISK_COLOR: Record<string, string> = { low: '#34C759', medium: '#FF9500', high: '#FF3B30' };

export function AdminBillingPage() {
  const nav = useNavigate();
  const [subs] = useState(SUBS);
  const [filter, setFilter] = useState<'all' | 'active' | 'past-due' | 'cancelled'>('all');

  const filtered = subs.filter((s) => filter === 'all' || s.status === filter);
  const totalMRR = subs.filter((s) => s.status === 'active').reduce((sum, s) => sum + s.mrr, 0);
  const pastDue = subs.filter((s) => s.status === 'past-due').length;
  const churnRisk = subs.filter((s) => s.churnRisk === 'high').length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Billing</div>
        <button onClick={() => alert('Exporting CSV')} data-testid="export-csv" className="px-3 text-[15px] text-[#0A84FF]">Export</button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3 rounded-[16px] border border-white/10 bg-gradient-to-br from-[#34C759] to-[#248A3D] p-4 text-center">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.85)' }}>Monthly recurring revenue</div>
        <div className="mt-1 text-[36px] font-bold tabular-nums">${totalMRR.toLocaleString()}</div>
        <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.85)' }}>{subs.filter((s) => s.status === 'active').length} active subscriptions</div>
      </div>

      {/* Stats */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        <Stat label="Past due"    value={pastDue}     color="#FF3B30" />
        <Stat label="Churn risk"  value={churnRisk}   color="#FF9500" />
        <Stat label="Churn rate"  value="2.4%"        color="#8E8E93" />
      </div>

      {/* Filter */}
      <div className="px-4 pt-3 flex items-center gap-1.5 overflow-x-auto">
        {(['all', 'active', 'past-due', 'cancelled'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`bfilter-${f}`}
            className={['shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize transition',
              filter === f ? 'bg-white text-black' : 'bg-white/10 text-white/70 active:opacity-40'
            ].join(' ')}
          >{f}</button>
        ))}
      </div>

      {/* Subs list */}
      <div className="px-4 mt-3 space-y-2">
        {filtered.map((s) => {
          const sm = STATUS_META[s.status];
          return (
            <div key={s.id} data-testid={`sub-${s.id}`} className="rounded-[14px] border border-white/10 bg-white/5 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold" style={{ backgroundColor: TIER_COLOR[s.tier] + '30', color: TIER_COLOR[s.tier] }}>{s.trader[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-bold text-white">{s.trader}</span>
                    <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ backgroundColor: sm.bg, color: sm.color }}>{s.status}</span>
                    {s.churnRisk === 'high' && <span className="rounded-full bg-[#FF3B30] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Risk</span>}
                  </div>
                  <div className="text-[11px] text-white/50 font-mono">{s.account} · ${(s.accountSize/1000).toFixed(0)}K {s.tier}</div>
                  <div className="text-[10px] text-white/50 mt-0.5">Started {s.started} · Next billing {s.nextBilling} · {s.failedPayments} failed</div>
                </div>
                <div className="text-right">
                  <div className="text-[16px] font-bold text-white tabular-nums">${s.mrr}/mo</div>
                  {s.failedPayments > 0 && <div className="text-[10px]" style={{ color: RISK_COLOR[s.churnRisk] }}>{s.churnRisk} risk</div>}
                </div>
              </div>
              <div className="mt-2 flex gap-1.5">
                {s.status === 'active' && (
                  <>
                    <button onClick={() => alert('Refund issued')} data-testid={`refund-${s.id}`} className="flex-1 h-7 rounded-full bg-[#FF9500] text-black text-[11px] font-bold active:opacity-40">Refund</button>
                    <button onClick={() => alert('Tier change initiated')} data-testid={`change-${s.id}`} className="flex-1 h-7 rounded-full bg-[#0A84FF] text-white text-[11px] font-bold active:opacity-40">Change tier</button>
                    <button onClick={() => alert('Subscription paused')} data-testid={`pause-${s.id}`} className="flex-1 h-7 rounded-full bg-white/10 text-white text-[11px] font-semibold active:opacity-40">Pause</button>
                  </>
                )}
                {s.status === 'past-due' && (
                  <>
                    <button onClick={() => alert('Retrying payment')} data-testid={`retry-${s.id}`} className="flex-1 h-7 rounded-full bg-[#FF3B30] text-white text-[11px] font-bold active:opacity-40">Retry payment</button>
                    <button onClick={() => alert('Dunning email sent')} data-testid={`dun-${s.id}`} className="flex-1 h-7 rounded-full bg-[#FF9500] text-black text-[11px] font-bold active:opacity-40">Dunning</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div className="rounded-[10px] bg-white/5 border border-white/10 p-2 text-center">
      <div className="text-[9px] text-white/60 uppercase">{label}</div>
      <div className="text-[16px] font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
